// ============================================================
// PublicQuotePage.jsx  — Customer-facing quote view/accept page
// Route: /quote/:token
// ============================================================
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RentalContractFlow from './RentalContractFlow';
import { taxLines } from './lib/taxLines';
import { daysBetween, describeRateLine } from './lib/rentalDays';

const API    = process.env.REACT_APP_API_URL || '';
const fmt    = n => '$' + Number(n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString('en-US',{weekday:'short',month:'long',day:'numeric',year:'numeric'}) : '—';

export default function PublicQuotePage() {
  const token = window.location.pathname.split('/quote/')[1]?.split('/')[0];
  const [quote,   setQuote]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [step,    setStep]    = useState('view'); // view | contract | success | declined
  const [order,   setOrder]   = useState(null);
  const [bizName, setBizName] = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid quote link.'); setLoading(false); return; }
    axios.get(`${API}/api/public/quotes/${token}`)
      .then(r => { setQuote(r.data.quote); setBizName(r.data.business_name || ''); setLoading(false); })
      .catch(() => { setError('Quote not found or has expired.'); setLoading(false); });
  }, [token]);

  async function acceptQuote() {
    try {
      const { data } = await axios.post(`${API}/api/public/quotes/${token}/accept`);
      setOrder(data.order);
      setStep('contract');
    } catch(e) {
      alert(e.response?.data?.error || 'Could not accept quote. Please try again.');
    }
  }

  async function declineQuote() {
    if (!window.confirm('Are you sure you want to decline this quote?')) return;
    await axios.post(`${API}/api/public/quotes/${token}/decline`);
    setStep('declined');
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#f97316', fontSize:24 }}>Loading...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color:'#e2e8f0' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>❌</div>
        <div style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>Quote Not Found</div>
        <div style={{ color:'#64748b' }}>{error}</div>
      </div>
    </div>
  );

  if (step === 'declined') return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color:'#e2e8f0' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>👋</div>
        <div style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>Quote Declined</div>
        <div style={{ color:'#64748b' }}>Thank you for letting us know. Feel free to reach out if your needs change.</div>
      </div>
    </div>
  );

  if (step === 'success') return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color:'#e2e8f0', maxWidth:400 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
        <div style={{ fontSize:24, fontWeight:700, marginBottom:8 }}>You're All Set!</div>
        <div style={{ color:'#64748b', marginBottom:16 }}>Your rental agreement is signed and your deposit has been collected. We'll be in touch to confirm your delivery.</div>
        <div style={{ color:'#4ade80', fontSize:14 }}>📧 Check your email for your signed contract.</div>
      </div>
    </div>
  );

  if (step === 'contract' && order) return (
    <RentalContractFlow
      order={order}
      prefill={{ signer_name: quote.client_name, signer_email: quote.client_email, signer_phone: quote.client_phone }}
      apiBase={`${API}/api/public/rentals`}
      onClose={() => setStep('view')}
      onComplete={() => setStep('success')}
    />
  );

  const days = daysBetween(quote.from_date, quote.to_date) || null; // inclusive — same count that priced the quote
  const isExpired  = quote.expiry_date && new Date(quote.expiry_date) < new Date();
  const isAccepted = quote.status === 'accepted';
  const isDeclined = quote.status === 'declined';
  const canAct     = !isExpired && !isAccepted && !isDeclined;

  return (
    <div style={{ fontFamily:"'DM Sans',Arial,sans-serif", background:'#0a0a0f', minHeight:'100vh', color:'#e2e8f0', padding:'24px 16px' }}>
      <div style={{ maxWidth:600, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🚧</div>
          <div style={{ fontWeight:800, fontSize:22, color:'#fff' }}>{bizName || 'Equipment Rentals'}</div>
          <div style={{ color:'#64748b', fontSize:13, marginTop:4 }}>Equipment Rental Quote</div>
        </div>

        {/* Status banner */}
        {isExpired  && <div style={{ background:'#3b0a0a', border:'1px solid #f87171', borderRadius:8, padding:'10px 16px', marginBottom:16, color:'#f87171', fontWeight:600, textAlign:'center' }}>⏰ This quote has expired</div>}
        {isAccepted && <div style={{ background:'#14532d', border:'1px solid #4ade80', borderRadius:8, padding:'10px 16px', marginBottom:16, color:'#4ade80', fontWeight:600, textAlign:'center' }}>✅ Quote Accepted — Order #{quote.converted_order_id}</div>}
        {isDeclined && <div style={{ background:'#252840', border:'1px solid #64748b', borderRadius:8, padding:'10px 16px', marginBottom:16, color:'#94a3b8', fontWeight:600, textAlign:'center' }}>Quote Declined</div>}

        {/* Quote card */}
        <div style={{ background:'#252838', border:'1px solid #2e3150', borderRadius:16, overflow:'hidden' }}>
          {/* Customer info */}
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #2e3150' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:6 }}>Prepared for</div>
            <div style={{ fontWeight:700, fontSize:18, color:'#fff' }}>{quote.client_name}</div>
            {quote.client_company && <div style={{ color:'#64748b', fontSize:13 }}>{quote.client_company}</div>}
            {quote.delivery_address && <div style={{ color:'#64748b', fontSize:12, marginTop:4 }}>📍 {quote.delivery_address}</div>}
          </div>

          {/* Rental period */}
          {quote.from_date && (
            <div style={{ padding:'12px 20px', borderBottom:'1px solid #2e3150', display:'flex', gap:24 }}>
              <div>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:2 }}>Pickup</div>
                <div style={{ fontWeight:600, color:'#e2e8f0' }}>{fmtDate(quote.from_date)}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:2 }}>Return</div>
                <div style={{ fontWeight:600, color:'#e2e8f0' }}>{fmtDate(quote.to_date)}</div>
              </div>
              {days && <div>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:2 }}>Duration</div>
                <div style={{ fontWeight:600, color:'#f97316' }}>{days} day{days!==1?'s':''}</div>
              </div>}
            </div>
          )}

          {/* Line items */}
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #2e3150' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10 }}>Equipment</div>
            {(quote.rental_quote_items||[]).map((item, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #1e2030' }}>
                <div>
                  <div style={{ fontWeight:600, color:'#e2e8f0' }}>{item.item_name}</div>
                  <div style={{ fontSize:12, color:'#64748b' }}>
                    {describeRateLine(item, days || 1)}
                  </div>
                </div>
                <div style={{ fontWeight:700, color:'#e2e8f0' }}>{fmt(item.total_price)}</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #2e3150' }}>
            {Number(quote.mileage_charge||quote.delivery_fee) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#94a3b8', marginBottom:6 }}>
                <span>Delivery ({quote.delivery_miles}mi round trip)</span>
                <span>{fmt(quote.mileage_charge||quote.delivery_fee)}</span>
              </div>
            )}
            {taxLines({ ...quote, delivery_fee: 0, mileage_charge: 0 }, { includeSubtotal: false, includeTotal: false }).map(row => (
              <div key={row.key} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color: row.kind === 'discount' ? '#4ade80' : '#94a3b8', marginBottom:6 }}>
                <span>{row.label}</span><span>{row.kind === 'discount' ? '-' + fmt(-row.value) : fmt(row.value)}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:20, fontWeight:800, color:'#f97316', paddingTop:8, borderTop:'1px solid #2e3150', marginTop:4 }}>
              <span>Total</span><span>{fmt(quote.total)}</span>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #2e3150', fontSize:13, color:'#64748b', lineHeight:1.6 }}>
              {quote.notes}
            </div>
          )}

          {/* Expiry */}
          <div style={{ padding:'12px 20px', borderBottom: canAct ? '1px solid #2e3150' : 'none', fontSize:12, color:'#4a5568', textAlign:'center' }}>
            {canAct ? `This quote expires on ${fmtDate(quote.expiry_date)}` : ''}
          </div>

          {/* Actions */}
          {canAct && (
            <div style={{ padding:'20px', display:'flex', gap:12, flexDirection:'column' }}>
              <button onClick={acceptQuote}
                style={{ width:'100%', padding:'16px', borderRadius:10, border:'none', background:'#f97316', color:'#fff', fontWeight:800, fontSize:16, cursor:'pointer' }}>
                ✅ Accept Quote & Sign Agreement
              </button>
              <button onClick={declineQuote}
                style={{ width:'100%', padding:'12px', borderRadius:10, border:'1px solid #3a3f60', background:'transparent', color:'#64748b', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                Decline Quote
              </button>
            </div>
          )}
        </div>

        <div style={{ textAlign:'center', marginTop:20, fontSize:12, color:'#2e3150' }}>
          Powered by <span style={{ color:'#f97316' }}>iVision CRM</span>
        </div>
      </div>
    </div>
  );
}
